import React from "react";
import { Link } from "react-router-dom";

const CheckIcon = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

/* Pillar icons — SVG for consistency and accessibility */
const ServerIcon = () => (
  <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
  </svg>
);

const AIIcon = () => (
  <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const FDEIcon = () => (
  <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const PILLARS = [
  {
    id: "managed",
    borderColor: "#1d4ed8",
    iconBg: "#dbeafe",
    iconColor: "#1d4ed8",
    Icon: ServerIcon,
    labelBg: "#1e3a8a",
    label: "Managed Services",
    tagline: "Keep your existing stack running — seamlessly.",
    href: "/services/managed-services",
    cta: "Managed Services →",
    items: [
      "24x7 monitoring & support",
      "Application maintenance & upgradation",
      "Performance, security & compliance",
      "Delivery mechanisms that ensure reliability and continuity",
    ],
  },
  {
    id: "ai",
    borderColor: "#15803d",
    iconBg: "#dcfce7",
    iconColor: "#15803d",
    Icon: AIIcon,
    labelBg: "#166534",
    label: "Enterprise AI",
    tagline: "Add intelligence. Unlock value.",
    href: "/enterprise-ai",
    cta: "Enterprise AI →",
    items: [
      "AI as an analytics & intelligence layer",
      "Predictive insights & automation",
      "Generative AI for operations & CX",
      "Research-driven, secure & scalable AI frameworks",
    ],
  },
  {
    id: "fde",
    borderColor: "#7c3aed",
    iconBg: "#ede9fe",
    iconColor: "#7c3aed",
    Icon: FDEIcon,
    labelBg: "#5b21b6",
    label: "Forward Deployed Engineers (FDE)",
    tagline: "Our engineers. On your site. Driving deep integration.",
    href: "/services/dedicated-engineering-teams",
    cta: "Deploy an FDE Team →",
    items: [
      "On-site AI engineers & architects",
      "Seamless integration with existing systems",
      "Faster delivery, contextual understanding",
      "Embedded teams. Real outcomes.",
    ],
  },
];

const NewThreePillars = () => (
  <section className="pillars-v2" aria-labelledby="pillars-heading" id="three-pillars">
    <div className="container">
      <p className="section-eyebrow">Our Three Pillars</p>
      <h2 id="pillars-heading" className="section-title">
        We don't ask you to choose between modernising or maintaining.
      </h2>
      <p className="section-title" style={{ color: "#f37417", fontSize: "clamp(1.2rem,2.5vw,1.75rem)", fontWeight: 800, marginBottom: 0 }}>
        We do both simultaneously.
      </p>
      <p className="section-sub mt-3">
        Most technology companies force a choice — replace or maintain. Avani Cimcon's enterprise software engineering model supports all three phases of your technology journey at the same time, through process-driven delivery mechanisms and ongoing investments in upgradation and research.
      </p>

      <div className="row">
        {PILLARS.map((p) => (
          <div key={p.id} className="col-lg-4 mb-4">
            <article
              className="pillar-card"
              style={{ borderTopColor: p.borderColor }}
            >
              <div
                className="pillar-card-icon"
                style={{ background: p.iconBg, color: p.iconColor }}
                aria-hidden="true"
              >
                <p.Icon />
              </div>

              <span
                className="pillar-card-label"
                style={{ background: p.labelBg, color: "#fff" }}
              >
                {p.label}
              </span>

              <p className="pillar-card-tagline">{p.tagline}</p>

              <ul className="pillar-card-list" aria-label={`${p.label} capabilities`}>
                {p.items.map((item) => (
                  <li key={item}>
                    <CheckIcon />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="pillar-card-divider" aria-hidden="true" />

              <Link to={p.href} className="pillar-card-cta" aria-label={`Learn more about ${p.label}`}>
                {p.cta}
              </Link>
            </article>
          </div>
        ))}
      </div>
      <div className="text-center mt-2">
        <Link to="/services/legacy-modernization" className="hero-v2-btn-outline" style={{ display: "inline-block" }}>
          View the Modernization Framework →
        </Link>
      </div>
    </div>
  </section>
);

export default NewThreePillars;
